(() => {
  const STORE = 'ollihess.de';
  /* Cache in sessionStorage: Collection-Seiten fetchen pro Karte; ohne Cache
     wuerde jede Navigation dieselben Produkte erneut abfragen. 0-Ergebnisse
     werden mitgecacht (Produkte ohne Reviews sollen nicht pro Seite neu feuern). */
  const CACHE_TTL = 6 * 60 * 60 * 1000;
  const inflight = new Map();
  const seen = new WeakSet();

  function readCache(id) {
    try {
      const raw = sessionStorage.getItem('oh-rio:' + id);
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (!v || typeof v.t !== 'number' || Date.now() - v.t > CACHE_TTL) return null;
      return v;
    } catch (e) { return null; }
  }

  function writeCache(id, avg, count) {
    try {
      sessionStorage.setItem('oh-rio:' + id, JSON.stringify({ a: avg, c: count, t: Date.now() }));
    } catch (e) {}
  }

  function pluralTemplate(el, count) {
    if (count === 0) return el.dataset.countZero || '';
    const tpl = count === 1 ? el.dataset.countOne : el.dataset.countOther;
    return (tpl || '').replace('__COUNT__', count);
  }

  function formatValue(avg) {
    const s = (Math.round(avg * 10) / 10).toFixed(1);
    return (document.documentElement.lang || 'de').startsWith('de') ? s.replace('.', ',') : s;
  }

  function update(el, average, count) {
    const countEl = el.querySelector('.oh-rating__count');
    const srEl = el.querySelector('.oh-rating__count-sr');
    const starsEl = el.querySelector('.oh-rating__stars');
    const valueEl = el.querySelector('.oh-rating__value');
    const compactEl = el.querySelector('.oh-rating__count-compact');
    const countTextEl = el.querySelector('.oh-rating__count-text');
    if (countEl) countEl.textContent = `(${count})`;
    if (compactEl) compactEl.textContent = `(${count})`;
    if (valueEl) valueEl.textContent = formatValue(average);
    if (countTextEl) countTextEl.textContent = pluralTemplate(el, count);
    if (srEl) srEl.textContent = pluralTemplate(el, count);
    if (starsEl) {
      const fill = Math.max(0, Math.min(100, (average / 5) * 100));
      starsEl.style.setProperty('--oh-rating-fill', fill.toFixed(1) + '%');
    }
  }

  function fetchStats(id) {
    const cached = readCache(id);
    if (cached) return Promise.resolve(cached);
    if (inflight.has(id)) return inflight.get(id);
    const p = fetch(`https://api.reviews.io/product/review?store=${STORE}&sku=${encodeURIComponent(id)}&per_page=1`, {
      headers: { Accept: 'application/json' }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d || !d.stats) return null;
        const avg = parseFloat(d.stats.average);
        const count = parseInt(d.stats.count, 10);
        if (!Number.isFinite(count)) return null;
        writeCache(id, Number.isFinite(avg) ? avg : 0, count);
        return { a: Number.isFinite(avg) ? avg : 0, c: count };
      })
      .catch(() => null)
      .finally(() => inflight.delete(id));
    inflight.set(id, p);
    return p;
  }

  function fetchAndApply(el) {
    const id = el.dataset.productId;
    if (!id) return;
    fetchStats(id).then(v => {
      if (v && v.c > 0) update(el, v.a, v.c);
    });
  }

  /* ── Reviews.io-Popup ──
     Klick auf das Buy-Box-Rating öffnet die Produkt-Reviews im Modal
     (Reviews.io Polaris-Widget, lazy geladen). Schlägt das Laden fehl,
     greift der normale Anker-Sprung des Links (#shopify-product-reviews). */
  const POLARIS_SRC = 'https://widget.reviews.io/polaris/build.js';
  let polarisLoading = null;

  function loadPolaris() {
    if (window.ReviewsWidget) return Promise.resolve();
    if (!polarisLoading) {
      polarisLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = POLARIS_SRC;
        s.async = true;
        s.onload = resolve;
        s.onerror = () => { polarisLoading = null; reject(new Error('polaris load failed')); };
        document.head.appendChild(s);
      });
    }
    return polarisLoading;
  }

  function cssVar(name) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v ? `rgb(${v})` : '';
  }

  function ensureModal() {
    let fade = document.getElementById('oh-reviews-modal');
    if (fade) return fade;
    fade = document.createElement('div');
    fade.id = 'oh-reviews-modal';
    fade.hidden = true;
    fade.innerHTML =
      '<div class="oh-reviews-modal__box" role="dialog" aria-modal="true" aria-label="Bewertungen">' +
      '<button type="button" class="oh-reviews-modal__close" aria-label="Schließen">&times;</button>' +
      '<div class="oh-reviews-modal__widget" id="oh-reviews-modal-widget"></div>' +
      '</div>';
    document.body.appendChild(fade);
    const close = () => {
      fade.hidden = true;
      document.documentElement.classList.remove('oh-reviews-modal-open');
    };
    fade.addEventListener('click', (e) => { if (e.target === fade) close(); });
    fade.querySelector('.oh-reviews-modal__close').addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !fade.hidden) close(); });
    return fade;
  }

  function openPopup(productId) {
    return loadPolaris().then(() => {
      const fade = ensureModal();
      const mount = fade.querySelector('#oh-reviews-modal-widget');
      mount.innerHTML = '';

      const styles = {};
      const accent = cssVar('--accent');
      const text = cssVar('--text-color');
      const btnBg = cssVar('--button-background-primary');
      const btnText = cssVar('--button-text-primary');
      if (accent) styles['--common-star-color'] = accent;
      if (text) {
        styles['--heading-text-color'] = text;
        styles['--body-text-color'] = text;
        styles['--secondary-button-border-color'] = text;
        styles['--secondary-button-text-color'] = text;
      }
      if (btnBg) {
        styles['--primary-button-bg-color'] = btnBg;
        styles['--primary-button-border-color'] = btnBg;
      }
      if (btnText) styles['--primary-button-text-color'] = btnText;

      const lang = (document.documentElement.lang || 'de').slice(0, 2);
      new ReviewsWidget('#oh-reviews-modal-widget', {
        store: STORE,
        widget: 'polaris',
        options: {
          types: 'product_review',
          lang: lang,
          per_page: 5,
          product_review: { sku: productId, hide_if_no_results: false, min_rating: 1 },
          header: {
            enable_summary: true,
            enable_ratings: true,
            enable_image_gallery: true,
            enable_write_review: true,
            always_show: true
          },
          reviews: {
            enable_avatar: true,
            enable_reviewer_name: true,
            enable_verified_badge: true,
            enable_images: true,
            enable_ratings: true,
            enable_date: true
          }
        },
        styles: styles
      });

      fade.hidden = false;
      document.documentElement.classList.add('oh-reviews-modal-open');
    });
  }

  function bindPopup(el) {
    el.addEventListener('click', (e) => {
      const id = el.dataset.productId;
      if (!id) return;
      e.preventDefault();
      openPopup(id).catch(() => {
        window.location.href = el.getAttribute('href') || '#shopify-product-reviews';
      });
    });
  }

  /* Karten laden lazy: erst wenn sie (fast) im Viewport sind. Unsichtbare
     Duplikate (Impact rendert das Rating mobil/desktop doppelt) schneiden
     nie und loesen daher keinen Request aus; der Cache versorgt sie beim
     naechsten Auftauchen. */
  let cardObserver = null;

  function observeCard(el) {
    if (!('IntersectionObserver' in window)) { fetchAndApply(el); return; }
    if (!cardObserver) {
      cardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          cardObserver.unobserve(entry.target);
          fetchAndApply(entry.target);
        });
      }, { rootMargin: '200px' });
    }
    cardObserver.observe(el);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.oh-rating[data-fetch-live]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      if (el.dataset.fetchLive === 'true') {
        fetchAndApply(el);
        bindPopup(el);
      } else {
        observeCard(el);
      }
    });
  }

  let scanTimer = null;

  function init() {
    scan(document);
    /* Impact laedt Karten per AJAX nach (Filter, Pagination, Quick-View) —
       neue Elemente einsammeln, debounced. */
    if ('MutationObserver' in window) {
      new MutationObserver(() => {
        if (scanTimer) clearTimeout(scanTimer);
        scanTimer = setTimeout(() => scan(document), 150);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
